import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import axios from "axios";

const initialState = {
	list: [],
	project: null,
	error: "",
	loading: false,
};

const extractMessages = (data) => {
	if (Array.isArray(data)) {
		return data;
	}
	if (Array.isArray(data?.messages)) {
		return data.messages;
	}
	if (Array.isArray(data?.result)) {
		return data.result;
	}
	return null;
};

const showSuccessMessages = (data, fallbackMessage) => {
	const messages = extractMessages(data);
	if (messages && messages.length) {
		messages.forEach((msg) => {
			if (msg?.status === "error") {
				toast.error(msg?.message || fallbackMessage?.error || "Project request failed");
			} else {
				toast.success(msg?.message || fallbackMessage?.success || "Project request successful");
			}
		});
	} else if (fallbackMessage?.success) {
		toast.success(fallbackMessage.success);
	}
};

const showErrorMessages = (errorData, fallbackMessage) => {
	const messages = extractMessages(errorData);
	if (messages && messages.length) {
		messages.forEach((msg) => toast.error(msg?.message || fallbackMessage));
	} else {
		toast.error(errorData?.message || fallbackMessage);
	}
};

const buildProjectPayload = ({
	ProjectId = 0,
	ProjectSiteId,
	ProjectName,
	ProjectDescription,
	ProjectManagerId,
	StartDate,
	EndDate,
	TotalBudget,
	DataStatus = 1,
}) => ({
	ProjectId,
	ProjectSiteId,
	ProjectName,
	ProjectDescription,
	ProjectManagerId,
	StartDate,
	EndDate,
	TotalBudget,
	DataStatus,
});

export const addSingleProject = createAsyncThunk(
	"project/saveProjectDetails",
	async (values, { rejectWithValue }) => {
		const payload = buildProjectPayload(values);
		try {
			const { data } = await axios.post("projects/ProjectDetailsSetup", payload);
			showSuccessMessages(data, {
				success: payload.ProjectId ? "Project updated" : "Project added",
			});
			return data;
		} catch (error) {
			const errorData = error?.response?.data;
			showErrorMessages(errorData, "Failed to save project");
			return rejectWithValue(
				errorData ?? {
					message: error?.message || "Failed to save project",
				}
			);
		}
	}
);

export const deleteProject = createAsyncThunk(
	"project/deleteProject",
	async (projectId, { rejectWithValue }) => {
		try {
			const { data } = await axios.post("projects/ProjectDetailsSetup", {
				ProjectId: projectId,
				DataStatus: 2,
				ProjectSiteId: null,
				ProjectName: null,
				ProjectDescription: null,
				ProjectManagerId: null,
				StartDate: null,
				EndDate: null,
				TotalBudget: null,
			});
			showSuccessMessages(data, { success: "Project deleted" });
			return data;
		} catch (error) {
			const errorData = error?.response?.data;
			showErrorMessages(errorData, "Failed to delete project");
			return rejectWithValue(
				errorData ?? {
					message: error?.message || "Failed to delete project",
				}
			);
		}
	}
);

export const loadSingleProject = createAsyncThunk(
	"project/loadSingleProject",
	async (id, { rejectWithValue }) => {
		try {
			const { data } = await axios.get(`projects/ProjectDetails/${id}`);
			return data;
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load project",
				}
			);
		}
	}
);

export const loadAllProject = createAsyncThunk(
	"project/loadAllProject",
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await axios.get("projects/ProjectDetails");
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load projects",
				}
			);
		}
	}
);

export const loadAllProjectByStatus = createAsyncThunk(
	"project/loadAllProjectByStatus",
	async (status, { rejectWithValue }) => {
		try {
			const { data } = await axios.get(`projects/ProjectDetails`, {
				params: status && status !== "all" ? { status } : {},
			});
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load projects",
				}
			);
		}
	}
);

export const updateProject = createAsyncThunk(
	"project/updateProject",
	async ({ id, values }, { rejectWithValue }) => {
		const payload = buildProjectPayload({
			...values,
			ProjectId: id,
			DataStatus: values?.DataStatus ?? 1,
		});
		try {
			const { data } = await axios.post("projects/ProjectDetailsSetup", payload);
			showSuccessMessages(data, { success: "Project updated" });
			return data;
		} catch (error) {
			const errorData = error?.response?.data;
			showErrorMessages(errorData, "Failed to update project");
			return rejectWithValue(
				errorData ?? {
					message: error?.message || "Failed to update project",
				}
			);
		}
	}
);

const projectSlice = createSlice({
	name: "project",
	initialState,
	reducers: {
		clearProject: (state) => {
			state.project = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(loadAllProject.pending, (state) => {
				state.loading = true;
				state.error = "";
			})
			.addCase(loadAllProject.fulfilled, (state, action) => {
				state.loading = false;
				state.list = Array.isArray(action.payload) ? action.payload : [];
			})
			.addCase(loadAllProject.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to load projects";
			})
			.addCase(loadAllProjectByStatus.pending, (state) => {
				state.loading = true;
				state.error = "";
			})
			.addCase(loadAllProjectByStatus.fulfilled, (state, action) => {
				state.loading = false;
				state.list = Array.isArray(action.payload) ? action.payload : [];
			})
			.addCase(loadAllProjectByStatus.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to load projects";
			})
			.addCase(addSingleProject.pending, (state) => {
				state.loading = true;
				state.error = "";
			})
			.addCase(addSingleProject.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(addSingleProject.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to save project";
			})
			.addCase(loadSingleProject.pending, (state) => {
				state.loading = true;
				state.error = "";
			})
			.addCase(loadSingleProject.fulfilled, (state, action) => {
				state.loading = false;
				state.project = action.payload?.data ?? action.payload ?? null;
			})
			.addCase(loadSingleProject.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to load project";
			})
			.addCase(updateProject.pending, (state) => {
				state.loading = true;
				state.error = "";
			})
			.addCase(updateProject.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(updateProject.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to update project";
			})
			.addCase(deleteProject.pending, (state) => {
				state.loading = true;
				state.error = "";
			})
			.addCase(deleteProject.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(deleteProject.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to delete project";
			});
	},
});

export default projectSlice.reducer;
export const { clearProject } = projectSlice.actions;
